import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
    maximumBrandImageHeight = "maximumBrandImageHeight",
    backgroundColorGradient = "backgroundColorGradient",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumNumberOfBrandsPerRow]: string;
        [fields.maximumBrandImageHeight]: number;
        [fields.backgroundColorGradient]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        randomize: props.fields.brandsToDisplay === "random",
        pageSize: parseInt(props.fields.numberOfBrandsToDisplay || "0", 10),
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;
    const brandGalleryPageLink = getPageLinkByPageType(state, "BrandsPage");

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
        brandGalleryPageLinkURL: brandGalleryPageLink?.url,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface CENBrandListStyles {
    titleText?: TypographyProps;
    brandContainer?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
    brandTitleSection?: InjectableCss;
    brandLink?: InjectableCss;
    container?: InjectableCss;
    brandLinkMobile?: InjectableCss;
    gradientContainer?: InjectableCss;
}

export const listStyles: CENBrandListStyles = {
    titleText: {
        variant: "h5",
        transform: "inherit",
        css: css`
            margin: 0;
            font-size: ${TYPOGRAPHY.size.large} !important;
            letter-spacing: 0em;
            text-transform: uppercase;
        `,
    },
    imageItem: {
        css: css`
            justify-content: left;
            flex-basis: 50% !important;
            max-width: 50% !important;
            margin-bottom: 16px !important;
            @media (min-width: 768px) and (max-width: 991px) {
                align-items: flex-start;
            }
            @media (min-width: 768px) {
                &:nth-child(2n + 1) {
                    padding-right: 8px;
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-basis: 100% !important;
                            max-width: 100% !important;
                            &:last-child {
                                margin-bottom: 24px !important;
                            }
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    brandContainer: {
        css: css`
            padding: 32px;
            border: 4px solid ${COLORS.background.grayShade};
            margin-bottom: 100px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 24px;
                    `,
                    css`
                        margin-bottom: 24px;
                    `,
                    css`
                        margin-bottom: 74px;
                        padding: 16px;
                    `,
                    null,
                ]);
            }}
        `,
    },
    brandLink: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: 16px;
            color: ${getColor("link.base")};
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    brandTitleSection: {
        css: css`
            display: flex;
            max-width: 100%;
            flex-basis: 100%;
            padding: 0;
            justify-content: space-between;
            margin-bottom: 32px !important;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 24px !important;
                        `,
                        css`
                            margin-bottom: 24px !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    container: {
        css: css`
            height: 175px;
            overflow: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            height: auto;
                            overflow: hidden;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
            > div[class^="GridOffset"] {
                width: 100%;
                margin: 0;
                > div[class^="GridItemStyle"] {
                    padding: 0;
                }
            }
        `,
    },
    imageContainer: {
        css: css`
            justify-content: flex-start;
            width: 100%;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: block;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    link: {
        css: css`
            span {
                color: ${getProp("theme.typography.p.color")};
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.smaller};
                        `,
                        null,
                    ]);
                }}
            }
        `,
    },
    brandLinkMobile: {
        css: css`
            display: none;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: block;
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            font-size: ${TYPOGRAPHY.size.smaller};
                            line-height: 16px;
                            color: color: ${getColor("link.base")};
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    gradientContainer: {
        css: css`
            box-shadow: 0px 4px 8px rgba(38, 37, 37, 0.24);
            height: 16px;
        `,
    },
};

const styles = listStyles;

class CENBrandGallery extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandGallery, shouldLoadBrandGallery, brandGalleryParameter, fields } = this.props;
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }

    render() {
        const {
            brandsState: { isLoading, value: brandList },
            fields,
            brandGalleryPageLinkURL,
        } = this.props;

        if (isLoading || !brandList) {
            return null;
        }

        const desktop = parseInt(fields.maximumNumberOfBrandsPerRow, 10);
        const tablet = Math.ceil(desktop / 2);
        const mobile = Math.ceil(tablet / 2);

        const imgProps = {
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: `${fields.maximumBrandImageHeight}px`,
            },
        };

        const focusFunction = () => {
            const elementToFocus = document.getElementById("collapseButton");
            if (elementToFocus) {
                elementToFocus.focus();
            } else {
                document.getElementById("expandButton")?.focus();
            }
        };

        return (
            <StyledWrapper>
                <StyledWrapper
                    {...styles.gradientContainer}
                    data-test-selector="gradientContainer"
                    style={{ background: fields.backgroundColorGradient }}
                ></StyledWrapper>
                <StyledWrapper {...styles.brandContainer} data-test-selector="CENbrandGallery">
                    <GridItem {...styles.brandTitleSection}>
                        <VisuallyHidden as="h1">{translate("Explore available brands")}</VisuallyHidden>
                        {this.props.fields.title && (
                            <Typography
                                variant="headerSecondary"
                                {...styles.titleText}
                                data-test-selector="CENbrandGalleryTitle"
                            >
                                {this.props.fields.title}
                            </Typography>
                        )}
                        <Link href={brandGalleryPageLinkURL} {...styles.brandLink}>
                            {translate("Shop all Brands")}
                            <span> &#8594; </span>
                        </Link>
                    </GridItem>
                    <GridContainer {...styles.container}>
                        <FlexWrapContainer {...styles.imageContainer}>
                            {brandList.map(brand => (
                                <FlexItem
                                    key={brand.id}
                                    flexColumns={[mobile, tablet, tablet, desktop, desktop]}
                                    {...styles.imageItem}
                                >
                                    {brand === brandList[0] && (
                                        <SkipNav
                                            extendedStyles={styles.skipToContent}
                                            text={translate("Skip to full brand list")}
                                            focusFunction={focusFunction}
                                        />
                                    )}
                                    <Link href={brand.detailPagePath} {...styles.link}>
                                        <Typography>{brand.name}</Typography>
                                    </Link>
                                </FlexItem>
                            ))}
                            <Link href={brandGalleryPageLinkURL} {...styles.brandLinkMobile}>
                                {translate("Shop all Brands")}
                                <span> &#8594; </span>
                            </Link>
                        </FlexWrapContainer>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CENBrandGallery),
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "CEN Brand Gallery",
        allowedContexts: ["BrandsPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.backgroundColorGradient,
                displayName: "Background Color",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "linear-gradient(90deg, #266b40 27.08%, #2d804c 71.87%, #43687a 100%)",
            },
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Random",
                        value: "random",
                    },
                    {
                        displayName: "Select brands",
                        value: "select-brands",
                    },
                ],
                defaultValue: "random",
                fieldType: "General",
                tooltip:
                    "Brand logos can be picked at random on page load, or you can select specific brands to display.",
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "DropDownField",
                defaultValue: "12",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                ],
                fieldType: "General",
                isVisible: item => item.fields.brandsToDisplay === "random",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
                defaultValue: [],
                fieldType: "General",
            },
            {
                name: fields.maximumNumberOfBrandsPerRow,
                displayName: "Maximum brands per row",
                editorTemplate: "DropDownField",
                defaultValue: "6",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                ],
                fieldType: "General",
                tooltip:
                    "Up to 6 brands can display per row. This number should be adjusted based on the number of brands you choose to display. For example, if you display 10 brands you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
            },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "General",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
        ],
    },
};

export default widgetModule;
